import { cn } from "@/lib/utils";

// The semantic tint for a job's scheduling state. Per "faded, not flagged"
// (ux-principles §15 / ADR-0019) normal lifecycle states never use danger/
// warning: active states read accent, done reads success, terminal/no-date
// reads muted. Color appears ONLY inside the chip (§4.4 — semantic color is
// never brand chrome).
type ChipTint = "accent" | "success" | "muted";

export const JOB_STATUS_TINT: Record<string, ChipTint> = {
  new: "muted",
  scheduled: "accent",
  in_progress: "accent",
  completed: "success",
  canceled: "muted",
};

export const BORDER: Record<ChipTint, string> = {
  accent: "border-l-[color:var(--color-accent)]",
  success: "border-l-[color:var(--color-success)]",
  muted: "border-l-[color:var(--color-text-lo)]",
};

export interface CalendarJob {
  id: string;
  label: string;
  status: string;
  canceled: boolean;
  crew?: string | null;
  customer?: string | null;
  jobNumber?: string | null;
  productionStartDate?: string | null;
  productionEndDate?: string | null;
  hasCrewConflict?: boolean;
}

/**
 * A single job event chip rendered into a calendar day cell — the shared chip
 * for the month grid (and, slice 2, the week grid + overflow popover). A
 * tint-coded left rule + the job label; the whole chip is a click/keyboard
 * target to the job detail (§3.1 — never hover-only). No imagery (§I2 — the
 * grid is denser than the list rows that already ban heroes).
 */
export function JobChip({
  job,
  onOpen,
}: {
  job: CalendarJob;
  onOpen: () => void;
}) {
  const tint = JOB_STATUS_TINT[job.status] ?? "muted";
  return (
    <button
      type="button"
      onClick={onOpen}
      title={job.label}
      className={cn(
        "block w-full truncate rounded-[var(--radius-sm)] border-l-2 bg-[color:var(--color-surface)] px-1.5 py-0.5 text-left text-xs text-[color:var(--color-text-md)] hover:bg-[color:var(--color-elevated)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]",
        BORDER[tint],
        job.canceled && "text-[color:var(--color-text-lo)] line-through",
      )}
    >
      {job.label}
    </button>
  );
}
